import {
    Box,
    /* Divider, */ TextField,
    Typography,
    InputAdornment,
    IconButton,
    CircularProgress,
    FormControl,
    /* FormHelperText */ Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Link,
    Chip,
    Button,
    OutlinedInput,
} from 'components/input';
import { MdOutlineVisibility as Visibility, MdOutlineVisibilityOff as VisibilityOff } from 'react-icons/md';
import { useContext } from 'react';
import type { ChangeEvent, FormEvent, KeyboardEvent, CSSProperties } from 'react';
import { ReactComponent as BrandLogo } from '../assets/imagotipo.svg';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from 'GlobalContext';
import 'dayjs/locale/es'; // Importa el locale español
import { useLoginForm } from 'hooks/auth/useLoginForm';
import { useBuildInfoVersion } from 'hooks/auth/useBuildInfoVersion';
import { useGatewayHealthStatus } from 'hooks/auth/useGatewayHealthStatus';

interface LoginProps {
    redirect?: string;
}

type HealthStatusValue = boolean | 'loading';

export const Login = ({ redirect }: LoginProps) => {
    const { appVersion } = useBuildInfoVersion();
    const healthStatus = useGatewayHealthStatus() as Record<string, HealthStatusValue>;

    const { t } = useTranslation();
    const { setAuthenticated } = useContext(GlobalContext);
    const {
        email,
        setEmail,
        password,
        setPassword,
        loading,
        showPassword,
        setShowPassword,
        otpModalOpen,
        otpValue,
        setOtpValue,
        otpError,
        setOtpError,
        submitLogin,
        closeOtpModal,
        handleOtpSubmit,
    } = useLoginForm({ setAuthenticated, t });

    const logoStyles: CSSProperties = {
        width: '100%',
        maxWidth: 'none',
        height: 'auto',
        marginTop: '0.25rem',
        '--background-fill': '#1d9bf0',
        '--letters-fill': '#ffffff',
    };

    return (
        <Box
            className="w-[500px] rounded-3xl border border-[#343741] bg-[#1f1f1f]"
        >
            <Box
                component="form"
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    submitLogin({ event });
                }}
                className="flex flex-col px-7 pb-5 pt-6"
            >
                {/* Logo */}
                <Box className="flex w-full justify-center">
                    <BrandLogo aria-label="logo-app" style={logoStyles} />
                </Box>
                <Box className="-mb-8 -mt-9 flex items-end justify-end pr-2">
                    <Typography variant="h4" fontWeight={400}>
                        Timekeeper
                    </Typography>
                </Box>
                <Box className="mb-2 -mt-1 flex">
                    <Typography variant="body1" color="text.secondary">
                        {t('sign_on.form.login_in_to_your_account')}
                    </Typography>
                </Box>

                {/* Usuario */}
                <FormControl className="mb-2" fullWidth variant="outlined">
                    <OutlinedInput
                        id="email"
                        name="email"
                        label={t('sign_on.form.user')}
                        floatingLabel
                        showPlaceholderOnFocus
                        fullWidth
                        value={email.value}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setEmail({
                                value: event.target.value,
                                error: false,
                                helperText: '',
                            });
                        }}
                        placeholder={t('sign_on.form.enter_your_username')}
                    />
                </FormControl>

                {/* Password */}
                <FormControl className="mb-2" fullWidth variant="outlined">
                    <OutlinedInput
                        id="outlined-adornment-password"
                        name="password"
                        label={t('sign_on.form.password')}
                        floatingLabel
                        showPlaceholderOnFocus
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        value={password.value}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setPassword({
                                value: event.target.value,
                                error: false,
                                helperText: '',
                            });
                        }}
                        placeholder="********"
                        error={password.error}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton aria-label={showPassword ? 'hide the password' : 'display the password'} onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />

                    {/* <FormHelperText>
						{password.helperText}
					</FormHelperText> */}
                </FormControl>

                {/* Remember + forgot */}
                <Box className="mb-2.5 flex w-full items-center justify-between">
                    <FormControlLabel control={<Checkbox size="small" defaultChecked />} label={<Typography variant="body2">{t('sign_on.form.logged_in')}</Typography>} />

                    <Link className="text-[15px] normal-case no-underline hover:underline hover:underline-offset-2">
                        {t('sign_on.form.forgot_password')}
                    </Link>
                </Box>

                {/* Submit */}
                <Button type="submit" fullWidth variant="contained" disableElevation loading={loading} disabled={email.error || password.error} size="medium" className="h-11">
                    {t('sign_on.form.sign_on')}
                </Button>

                <Button
                    fullWidth
                    variant="outlined"
                    className="mt-1.5 h-11"
                    onClick={() => {
                        const next = encodeURIComponent(redirect || '/');
                        window.location.assign(`/authorize?next=${next}`);
                    }}
                >
                    Entrar con GoodRabbit
                </Button>

                {/* Divider */}
                {/* <Divider sx={{ mb: 3 }} /> */}

                {/* Register */}
                {/* <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <Link variant="text" size="small" sx={{ textTransform: 'none' }}>
                        {t('sign_on.form.no_account')}
                    </Link>
                </Box> */}

                {/* Version */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    className="my-3 text-center"
                >
                    {t('sign_on.form.version')}: {appVersion}
                </Typography>

                {/* Health Status */}
                <Box className="flex w-full flex-wrap justify-center gap-x-1 gap-y-0.5">
                    {Object.keys(healthStatus).map((key) => (
                        <Box key={key} className="flex items-center gap-2 rounded px-2 py-0.5">
                            <Typography variant="caption" className="text-[15px]">
                                {key}
                            </Typography>

                            {healthStatus[key] === 'loading' ? (
                                <CircularProgress size={12} />
                            ) : (
                                <Chip component={'span'} color={healthStatus[key] === true ? 'success' : 'error'} />
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Dialog
                open={otpModalOpen}
                onClose={() => {
                    if (!loading) {
                        closeOtpModal();
                    }
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{t('sign_on.form.enter_otp') || 'Ingresa tu OTP'}</DialogTitle>
                <DialogContent>
                    <TextField
                        id="opt-id"
                        name="opt"
                        autoFocus
                        margin="dense"
                        label={t('sign_on.form.otp') || 'OTP'}
                        type="text"
                        fullWidth
                        value={otpValue}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setOtpValue(event.target.value);
                            setOtpError('');
                        }}
                        error={Boolean(otpError)}
                        helperText={otpError}
                        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleOtpSubmit();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeOtpModal} disabled={loading}>
                        {t('common.cancel') || 'Cancelar'}
                    </Button>
                    <Button onClick={handleOtpSubmit} variant="contained" disabled={loading || !otpValue.trim()}>
                        {t('common.confirm') || 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
